import Component from '../../js/_Component';

/** How far a user must drag on an item for it to visually flip to another item while dragging. */
const DRAG_CUTOFF = 0.5;

/** How far a user must drag on an item for it to flip to another item after releasing. */
const SWIPE_CUTOFF = 0.25;

export default class Carousel extends Component {
	/**
	 * Element to place created items within.
	 */
	private itemContainer: HTMLElement;

	/**
	 * An array of all items added to the carousel.
	 */
	private items: HTMLElement[] = [];

	/**
	 * The optional element containing dots representing each item.
	 */
	private dotsContainer: HTMLElement;

	/**
	 * Dot controls for the items.
	 */
	private dots: HTMLElement[] = [];

	/**
	 * The current item displayed first within the carousel.
	 */
	private currentItem = 0;

	/**
	 * How many items are displayed within the bounds of the carousel.
	 */
	private itemsDisplayed = 1;

	/**
	 * The X position of the current drag location.
	 */
	private dragX: number = null;

	/**
	 * The Y position of the current drag location.
	 */
	private dragY: number = null;

	/**
	 * The previous X position of the drag location.
	 */
	private lastX: number = null;

	/**
	 * The previous Y position of the drag location.
	 */
	private lastY: number = null;

	/**
	 * Whether or not the most recent drag is using touch.
	 */
	private usingTouch = false;

	constructor(element: HTMLElement) {
		super(element);

		this.itemContainer = this.element.querySelector('.c-carousel--items');
		this.dotsContainer = this.element.querySelector('.c-carousel--dots');

		// Detect focus within carousel items, and bring them into view, for keyboard navigaton.
		this.element.addEventListener('focusin', (e) => {
			const item = (e.target as HTMLElement).closest('.c-carousel--item') as HTMLElement;
			const index = this.items.indexOf(item);
			if (index >= 0) {
				this.goToItem(index);
			}
		});

		// Detect drag actions starting with the mouse.
		this.itemContainer.addEventListener('mousedown', (e) => {
			e.preventDefault();
			this.beginDrag(e, e.clientX, e.clientY);
			this.usingTouch = false;
		});

		// Detect drag actions starting with touch.
		this.itemContainer.addEventListener('touchstart', (e) => {
			this.beginDrag(e, e.touches[0].clientX, e.touches[0].clientY);
			this.usingTouch = true;
		});

		// Intercept clicking after a drag event to avoid opening links with the same action.
		this.itemContainer.addEventListener('click', (e) => this.handleClick(e), true);

		// Get existing items within the markup on initialization, and add them as items.
		Array.from(this.itemContainer.children).forEach((item) => {
			this.addItem(item as HTMLElement);
		});

		// Set initial transform and other side-effects of updating.
		this.updateTransform();
	}

	/**
	 * Sets the number of items to be displayed within the carousel.
	 */
	setItemsDisplayed(amount: number) {
		this.itemsDisplayed = amount;
		this.items.forEach((item) => {
			item.style.width = `${100 / this.itemsDisplayed}%`;
		});
		this.updateTransform();
	}

	/**
	 * Retrieves how many items are displayed in the carousel.
	 */
	getItemsDisplayed() {
		return this.itemsDisplayed;
	}

	/**
	 * Retrieves the index of the first item.
	 */
	getCurrentItem() {
		return this.currentItem;
	}

	/**
	 * Retrieves the number of items in the carousel.
	 */
	getItemCount() {
		return this.items.length;
	}

	/**
	 * Dynamically add a new item to the carousel.
	 * @param item The element to add.
	 */
	addItem(item: HTMLElement) {
		const newItem = document.createElement('div');
		newItem.classList.add('c-carousel--item');
		newItem.style.width = `${100 / this.itemsDisplayed}%`;
		newItem.appendChild(item);
		this.items.push(newItem);
		this.itemContainer.appendChild(newItem);
		if (this.dotsContainer) {
			const dot = document.createElement('button');
			const dotIndex = this.dots.length;

			// If there is only one dot hide the dots
			// We're going to push into the array in the bit, so 0 is basically same as 1.
			if (this.dots.length === 0) {
				this.dotsContainer.classList.add('c-carousel--dots-hidden');
			} else {
				this.dotsContainer.classList.remove('c-carousel--dots-hidden');
			}

			// Set classes and attributes
			dot.setAttribute('tabindex', dotIndex === 0 ? '0' : '-1');
			dot.setAttribute('aria-label', `Go to slide ${dotIndex + 1}`);
			dot.classList.add('c-carousel--dot');
			if (dotIndex === 0) {
				dot.classList.add('m-active');
			}

			// Set event listeners
			dot.addEventListener('click', () => this.goToItem(dotIndex));
			dot.addEventListener('keydown', (event) => {
				if (event.key === 'ArrowLeft') {
					this.goToItem(dotIndex - 1);
					this.focusDot(dotIndex - 1);
				} else if (event.key === 'ArrowRight') {
					this.goToItem(dotIndex + 1);
					this.focusDot(dotIndex + 1);
				}
			});

			// Add dot to the array and the DOM
			this.dots.push(dot);
			this.dotsContainer.appendChild(dot);
		}
	}

	/**
	 * Moves the carousel to the next item.
	 */
	nextItem() {
		this.currentItem += 1;
		this.updateTransform();
		this.element.dispatchEvent(new CustomEvent('carouselitemchanged'));
	}

	/**
	 * Moves the carousel to the previous item.
	 */
	prevItem() {
		this.currentItem -= 1;
		this.updateTransform();
		this.element.dispatchEvent(new CustomEvent('carouselitemchanged'));
	}

	/**
	 * Scrolls a specific item into view on the carousel.
	 * @param index The index of the item to scroll into view.
	 */
	goToItem(index) {
		// Destination item is further to the left than the first displayed item.
		if (index < this.currentItem) {
			this.currentItem = index; // Make destination item the current (first) item.
		}

		// Destination item is further to the right than the last displayed item.
		const lastDisplayedIndex = this.currentItem + this.itemsDisplayed - 1;
		if (index > lastDisplayedIndex) {
			// Make destination item the final (last) item.
			this.currentItem = index - this.itemsDisplayed + 1;
		}

		this.updateTransform();
		this.element.dispatchEvent(new CustomEvent('carouselitemchanged'));
	}

	/**
	 * Sends focus to a specific dot.
	 * @param index The index of the dot to focus.
	 */
	focusDot(index) {
		// Destination dot is further to the left than the first dot.
		if (index < this.currentItem) {
			this.dots[0].focus();
			return;
		}

		// Destination dot is further to the right than the last dot.
		if (index >= this.dots.length) {
			this.dots[this.dots.length - 1].focus();
			return;
		}

		// Destination dot is available.
		this.dots[index].focus();
	}

	/**
	 * Update the transform applied to the carousel container based on the
	 * current item and number of displayed items.
	 */
	private updateTransform() {
		const maxIndex = this.items.length - this.itemsDisplayed;

		// Ensure the current item is within bounds.
		if (this.currentItem <= 0) {
			this.currentItem = 0;
		} else if (this.currentItem >= maxIndex) {
			this.currentItem = maxIndex;
		}

		this.updateMarks(this.currentItem); // Update class styling based on current item.

		// Transform based on the index and component scaling.
		this.itemContainer.style.transform = `translate(${this.currentItem * -100 / this.itemsDisplayed}%, 0)`;
	}

	/**
	 * Update class marks added to elements based on the position.
	 * @param relativeItem The intended 'currentItem' that classes will be updated relative to.
	 */
	private updateMarks(relativeItem: number) {
		// Mark items with classes based on their state.
		this.items.forEach((item, index) => {
			// Mark all as inactive first.
			if (this.dotsContainer) {
				this.dots[index].classList.remove('m-active');
				this.dots[index].setAttribute('tabindex', '-1');
			}
			item.classList.remove('m-out', 'm-out-left', 'm-out-right');

			// Mark everything before the first item as out to the left.
			if (index < relativeItem) {
				item.classList.add('m-out', 'm-out-left');

			// Mark everything after the last item as out to the right.
			} else if (index >= relativeItem + this.itemsDisplayed) {
				item.classList.add('m-out', 'm-out-right');

			// Mark the active item and anything else displayed as active.
			} else if (this.dotsContainer) {
				this.dots[index].classList.add('m-active');
				this.dots[index].setAttribute('tabindex', '0');
			}
		});
	}

	private beginDrag(_event: Event, x: number, y: number) {
		this.element.classList.remove('m-inactive');
		this.lastX = x;
		this.lastY = y;
		this.dragX = x;
		this.dragY = y;
		const continueDragMouse = (e: MouseEvent) => (
			this.continueDrag(e, e.clientX, e.clientY)
		);
		const continueDragTouch = (e: TouchEvent) => (
			this.continueDrag(e, e.touches[0].clientX, e.touches[0].clientY)
		);
		const endDragMouse = (e: MouseEvent) => (
			this.endDrag(e)
		);
		const endDragTouch = (e: TouchEvent) => (
			this.endDrag(e)
		);
		const cancelDragTouch = (e: TouchEvent) => (
			this.endDrag(e, true)
		);
		const clearEvents = () => {
			document.body.removeEventListener('mousemove', continueDragMouse);
			document.body.removeEventListener('touchmove', continueDragTouch);
			document.body.removeEventListener('mouseup', endDragMouse, true);
			document.body.removeEventListener('mouseleave', endDragMouse);
			document.body.removeEventListener('touchend', endDragTouch, true);
			document.body.removeEventListener('touchcancel', cancelDragTouch);
			this.element.removeEventListener('carouseldragend', clearEvents);
		};

		this.element.addEventListener('carouseldragend', clearEvents);
		document.body.addEventListener('mousemove', continueDragMouse);
		document.body.addEventListener('touchmove', continueDragTouch);
		document.body.addEventListener('mouseup', endDragMouse, true);
		document.body.addEventListener('mouseleave', endDragMouse);
		document.body.addEventListener('touchend', endDragTouch, true);
		document.body.addEventListener('touchcancel', cancelDragTouch);
	}

	private continueDrag(_e: Event, x: number, y: number) {
		this.lastX = x;
		this.lastY = y;
		this.updateMarks(this.currentItem + this.calculateDistanceFactor(x - this.dragX, DRAG_CUTOFF));
		this.itemContainer.style.transform = `translate(${this.currentItem * -100 / this.itemsDisplayed}%, 0) translate(${x - this.dragX}px, 0)`;
	}

	private endDrag(e: Event, ignoreClick: boolean = false) {
		e.preventDefault();
		this.element.classList.add('m-inactive');
		const delta = this.lastX - this.dragX;
		this.element.dispatchEvent(new CustomEvent('carouseldragend'));
		this.element.dispatchEvent(new CustomEvent('carouselitemchanged'));
		this.currentItem += this.calculateDistanceFactor(delta, SWIPE_CUTOFF);
		this.updateTransform(); // Reset transform after dragging, regardless of update.
		if (this.usingTouch && !ignoreClick) {
			(e.target as HTMLElement).click();
		}
	}

	private handleClick(e: Event) {
		// Use a larger distance tolerance for touch input.
		const distanceTolerance = this.usingTouch ? 6 : 1;

		// Distance dragged is more than tolerance (manhattan distance).
		if (
			(Math.abs(this.lastX - this.dragX) + Math.abs(this.lastY - this.dragY))
			> distanceTolerance
		) {
			e.preventDefault();
			return false;
		}

		return true;
	}

	/**
	 * Determines, given a drag distance, what the modification would be to the current item.
	 * @param distance The distance dragged, in pixels.
	 * @param cutoff The minimum distance that must be traveled, in items, for the factor to change
	 * from 0.
	 * @returns How far left (negatives) or right (positives) the current item would be moved to
	 * match the dragged distance.
	 */
	private calculateDistanceFactor(distance: number, cutoff: number): number {
		// Determine the width of a single item.
		const itemWidth = this.itemContainer.clientWidth / this.getItemsDisplayed();
		// Determine the distance dragged, in items.
		const itemDistance = distance / itemWidth;
		if (Math.abs(itemDistance) > cutoff) { // Dragged far enough to change position.
			// Note that dragging to negative direction results in a positive number, and vice versa.
			// Round away from zero (ceil if above, floor if below).
			return Math.ceil(Math.abs(itemDistance) - cutoff) * -Math.sign(itemDistance);
		}
		return 0; // Not far enough, no factor change.
	}
}

Component.register(Carousel, 'Carousel');
